import axios from "axios";
import { I18n } from "../hooks/useTranslations";

type ApiErrorBody = {
  error?: {
    status?: number;
    message?: string;
    error?: number | null;
    code?: number | null;
  };
};

export type CustomError = Error & {
  status?: number;
  json?: ApiErrorBody;
  error?: {
    status?: number;
    message?: string;
    error?: number | null;
    code?: number | null;
  };
  response?: {
    status?: number;
    data?: unknown;
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const toApiErrorBody = (value: unknown): ApiErrorBody | undefined => {
  if (!isRecord(value)) return undefined;
  const error = value["error"];
  if (!isRecord(error)) return value as ApiErrorBody;

  return {
    error: {
      status: typeof error["status"] === "number" ? error["status"] : undefined,
      message: typeof error["message"] === "string" ? error["message"] : undefined,
      error: typeof error["error"] === "number" ? error["error"] : null,
      code: typeof error["code"] === "number" ? error["code"] : null,
    },
  };
};

export const normalizeApiError = (error: unknown): CustomError => {
  if (axios.isAxiosError(error)) {
    const body = toApiErrorBody(error.response?.data);
    const status = error.response?.status ?? body?.error?.status;
    const code = body?.error?.code ?? body?.error?.error ?? null;
    const message = body?.error?.message ?? error.message;
    const customError = error as CustomError;

    customError.status = status;
    customError.message = message;
    customError.json = body;
    customError.error = {
      status,
      message,
      error: code,
      code,
    };

    return customError;
  }

  if (error instanceof Error) {
    return error as CustomError;
  }

  const customError = new Error("Unknown API error") as CustomError;
  customError.error = {
    message: customError.message,
    error: null,
  };
  return customError;
};

export const getErrorCode = (error: any): number | null => {
  // Try err.json.error.code (standard format)
  if (error?.json?.error?.code) {
    return error.json.error.code;
  }

  if (error?.response?.data?.error?.code) {
    return error.response.data.error.code;
  }

  // Try err.error.code (alternative format)
  if (error?.error?.code) {
    return error.error.code;
  }

  // Try parsing JSON from legacy error messages.
  if (error?.message && typeof error.message === "string") {
    try {
      const parsed = JSON.parse(error.message);
      if (parsed?.error?.code) {
        return parsed.error.code;
      }
    } catch (e) {
      // Not JSON or invalid format, ignore
    }
  }

  return null;
};

export const getErrorMessage = (i18n: I18n, error: any): string => {
  const code = getErrorCode(error);
  if (code) {
    const msg = i18n.errorsMap.get(code);
    return msg ?? `${i18n.t.common.genericError} (${code})`;
  }
  return i18n.t.common.genericError;
};

export const isConflict = (error: any): boolean =>
  error?.json?.error?.status === 409 || error?.response?.status === 409 || error?.status === 409;
